import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as RadioPrimitive from '@radix-ui/react-radio-group';
import { Check, Minus, Search, X } from 'lucide-react';
import { forwardRef, useId, type ReactNode } from 'react';
import { cn } from '../cn.js';

export const Checkbox = forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & { label?: ReactNode; description?: ReactNode }
>(function Checkbox({ className, label, description, id: providedId, ...props }, ref) {
  const generated = useId();
  const id = providedId ?? generated;
  const box = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={id}
      className={cn(
        'flex size-[18px] shrink-0 items-center justify-center rounded-[var(--pv-radius-xs)] border border-[var(--pv-line-strong)] bg-white',
        'transition-colors duration-[var(--pv-duration-fast)]',
        // Checked state carries the chamber's brand, not a fixed accent — see the note
        // in tokens.css about white-labelling.
        'data-[state=checked]:border-[var(--pv-brand)] data-[state=checked]:bg-[var(--pv-brand)] data-[state=checked]:text-[var(--pv-brand-on)]',
        'data-[state=indeterminate]:border-[var(--pv-brand)] data-[state=indeterminate]:bg-[var(--pv-brand)] data-[state=indeterminate]:text-[var(--pv-brand-on)]',
        'disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        {props.checked === 'indeterminate' ? (
          <Minus aria-hidden className="size-3" strokeWidth={3} />
        ) : (
          <Check aria-hidden className="size-3" strokeWidth={3} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (!label) return box;
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-px">{box}</span>
      <label htmlFor={id} className="cursor-pointer select-none">
        <span className="block [font-size:var(--pv-text-sm)] leading-snug">{label}</span>
        {description && (
          <span className="mt-0.5 block [font-size:var(--pv-text-xs)] leading-snug text-[var(--pv-faint)]">
            {description}
          </span>
        )}
      </label>
    </div>
  );
});

export const Switch = forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> & { label?: ReactNode }
>(function Switch({ className, label, id: providedId, ...props }, ref) {
  const generated = useId();
  const id = providedId ?? generated;
  const control = (
    <SwitchPrimitive.Root
      ref={ref}
      id={id}
      className={cn(
        'relative h-5 w-9 shrink-0 rounded-[var(--pv-radius-pill)] border border-[var(--pv-line-strong)] bg-[var(--pv-card-alt)]',
        'transition-colors duration-[var(--pv-duration-fast)]',
        'data-[state=checked]:border-[var(--pv-brand)] data-[state=checked]:bg-[var(--pv-brand)]',
        'disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="block size-[15px] translate-x-[2px] rounded-full bg-white shadow-[var(--pv-shadow-xs)] transition-transform duration-[var(--pv-duration-fast)] data-[state=checked]:translate-x-[18px]" />
    </SwitchPrimitive.Root>
  );
  if (!label) return control;
  return (
    <div className="flex items-center gap-2.5">
      {control}
      <label htmlFor={id} className="cursor-pointer select-none [font-size:var(--pv-text-sm)]">
        {label}
      </label>
    </div>
  );
});

export const RadioGroup = RadioPrimitive.Root;

export function RadioCard({
  value,
  title,
  description,
  icon,
}: {
  value: string;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cn(
        'group flex cursor-pointer items-start gap-3 rounded-[var(--pv-radius)] border border-[var(--pv-line-strong)] bg-white p-3.5',
        'transition-[border-color,box-shadow] duration-[var(--pv-duration-fast)] hover:border-[var(--pv-muted)]',
        'has-[[data-state=checked]]:border-[var(--pv-brand)] has-[[data-state=checked]]:bg-[var(--pv-brand-soft)] has-[[data-state=checked]]:shadow-[var(--pv-ring)]',
      )}
    >
      <RadioPrimitive.Item
        id={id}
        value={value}
        className="mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded-full border border-[var(--pv-line-strong)] bg-white data-[state=checked]:border-[var(--pv-brand)]"
      >
        <RadioPrimitive.Indicator className="size-2.5 rounded-full bg-[var(--pv-brand)]" />
      </RadioPrimitive.Item>
      <span className="min-w-0">
        <span className="flex items-center gap-2 [font-size:var(--pv-text-sm)] font-semibold">
          {icon}
          {title}
        </span>
        {description && (
          <span className="mt-1 block [font-size:var(--pv-text-xs)] leading-snug text-[var(--pv-muted)]">
            {description}
          </span>
        )}
      </span>
    </label>
  );
}

/**
 * The queue search box. Debouncing belongs to the caller, not here — this component
 * cannot know whether it is filtering 12 rows or querying 190,000.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  className,
  ...props
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <div className={cn('relative', className)}>
      <Search
        aria-hidden
        className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-[var(--pv-faint)]"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          'h-9 w-full rounded-[var(--pv-radius)] border border-[var(--pv-line-strong)] bg-white pl-8 pr-8 [font-size:var(--pv-text-sm)]',
          'placeholder:text-[var(--pv-faint)] focus:border-[var(--pv-leaf-deep)] focus:shadow-[var(--pv-ring)] focus:outline-none',
          '[&::-webkit-search-cancel-button]:hidden',
        )}
        {...props}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-[var(--pv-radius-xs)] p-0.5 text-[var(--pv-faint)] hover:text-[var(--pv-ink)]"
        >
          <X aria-hidden className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/** A removable filter token. Makes the active filter set visible and undoable. */
export function FilterChip({
  label,
  value,
  onRemove,
}: {
  label: string;
  value: ReactNode;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-[var(--pv-radius-sm)] border border-[var(--pv-line-strong)] bg-white py-1 pl-2 pr-1 [font-size:var(--pv-text-xs)]">
      <span className="text-[var(--pv-faint)]">{label}</span>
      <span className="font-medium">{value}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label} filter`}
        className="rounded-[var(--pv-radius-xs)] p-0.5 text-[var(--pv-faint)] hover:bg-[var(--pv-paper-sunk)] hover:text-[var(--pv-ink)]"
      >
        <X aria-hidden className="size-3" />
      </button>
    </span>
  );
}
